import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { color } from '@nuvolo/nuux-theme/utils';
import TMMButton from '../TMMButton';
import { CloseButton } from '#/app/components/AppMenu/Menu.styles';
import { FilterGroupOptions } from './ListProps';
import { FilterChip } from './util';
import styled from 'styled-components';
import { FilterGroup } from './filters';
import { NuvoBox } from '@nuvolo/nuux-core/components/NuvoBox';
import FAIcon from '#/common/components/FAIcon';

const FilterModalStyles = styled.div`
  /* Base drawer container */
  .filter-panel {
    position: fixed;
    top: 0;
    right: 0;
    height: 100%;
    background: ${color('custom', 'cardBackground')};
    color: ${color('custom', 'textColor')};
    z-index: 1501 !important;
    display: flex;
    flex-direction: column;
    box-shadow: -4px 0px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 240ms ease-out forwards;
  }

  /* MOBILE → fullscreen */
  @media (max-width: 480px) {
    .filter-panel {
      width: 100%;
      border-radius: 0; /* fullscreen look */
    }

    .filter-panel-footer {
      width: 100%;
      right: 0;
    }
  }

  /* TABLET/DESKTOP → right side drawer */
  @media (min-width: 480px) {
    .filter-panel {
      width: 380px; /* drawer width */
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
    }

    .filter-panel-footer {
      width: 380px;
      position: fixed;
      bottom: 0;
      right: 0;
      background: #fff;
      border-bottom-left-radius: 12px;
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0%);
    }
  }

  /* Header */
  .filter-panel-header {
    display: flex;
    align-items: center;
    padding: 20px;
    margin-top: 8px;
    height: 32px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0px;

    i {
      margin-right: 8px;
    }
  }

  .filter-panel-header h3 {
    margin: 0;
    font-size: 20px;
  }

  .filter-panel-header button {
    border: none;
    background: transparent;
    font-size: 22px;
    cursor: pointer;
  }

  /* Body scroll */
  .filter-panel-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    padding-bottom: 120px; /* prevent content behind footer */
  }

  /* Section title */
  .filter-panel-section-title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 12px;
    opacity: 0.75;
  }

  /* Checkbox size fix */
  .dx-checkbox-icon {
    width: 12px !important;
    height: 12px !important;
    font-size: 12px !important;
  }

  /* Footer buttons */
  .filter-panel-footer {
    padding: 16px 20px;
    display: flex;
    gap: 12px;
    background: ${color('custom', 'cardBackground')};
    color: ${color('custom', 'textColor')};
    z-index: 10000;
  }

  .filter-panel-footer button {
    flex: 1;
    padding: 14px;
    border-radius: 10px;
    border: none;
    font-size: 16px;
    cursor: pointer;
  }

  .filter-btn-clear {
    background-color: ${color('custom', 'lightContrastText')};
    color: ${color('ui', 'light2')};
  }

  .filter-btn-apply {
    background-color: ${color('custom', 'lightContrastText')};
    color: ${color('ui', 'light2')};
  }

  /* -------------------------------------------
   * OVERLAY (tablet/desktop only)
   * ----------------------------------------- */
  .filter-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1500;
    display: none; /* hidden on mobile */
  }

  @media (min-width: 480px) {
    .filter-overlay {
      display: block;
    }
  }
`;

export type FilterModalProps = {
  // isOpen: boolean;
  title?: string;
  groups: FilterGroupOptions[];
  activeFilters: FilterChip[];
  onApply: (newFilters: FilterChip[]) => void;
  onCancel: () => void;
};

const FilterModal = ({
  // isOpen,
  title,
  groups,
  activeFilters,
  onApply,
  onCancel,
}: FilterModalProps) => {
  const portal = document.getElementById('tmm-modal');

  const [draftFilters, setDraftFilters] = useState(activeFilters);

  const clearAllFilters = useCallback(() => {
    setDraftFilters([]);
  }, []);

  const onApplyButtonClick = useCallback(() => {
    onApply(draftFilters);
  }, [onApply, draftFilters]);

  const onFilterChange = useCallback((group: FilterGroupOptions, filters: FilterChip[]) => {
    setDraftFilters((oldFilters) => {
      const otherFilters = oldFilters.filter((f) => f.dataField !== group.dataField);
      return [...otherFilters, ...filters];
    });
  }, []);

  if (!portal) return <></>;

  return createPortal(
    <FilterModalStyles>
      {/* overlay for tablet/desktop; hidden on mobile via CSS */}
      <div className="filter-overlay" onClick={onCancel} />

      <div className="filter-panel">
        <div className="filter-panel-header">
          <FAIcon icon="filter" color={color('secondary', 'dark2')} />

          {title || 'Filter Items'}

          <CloseButton onClick={onCancel}>
            <FAIcon icon="xmark" color={color('secondary', 'dark2')} size="sm" />
          </CloseButton>
        </div>

        <div className="filter-panel-body">
          {groups?.map((group) => (
            <NuvoBox key={group.id} marginBottom={20}>
              <FilterGroup
                group={group}
                filters={draftFilters.filter((f) => f.dataField === group.dataField)}
                onFilterChange={(newFilters) => onFilterChange(group, newFilters)}
              />
            </NuvoBox>
          ))}
        </div>

        <div className="filter-panel-footer">
          <TMMButton
            width="50%"
            key="cancel"
            label="Clear All"
            type="normal"
            onClick={clearAllFilters}
          />
          <TMMButton
            width="50%"
            key="ok"
            label="Apply"
            type="submit"
            onClick={onApplyButtonClick}
          />
        </div>
      </div>
    </FilterModalStyles>,
    portal,
  );
};

export default React.memo(FilterModal) as typeof FilterModal;
